/**
 * useVoiceRecorder — React hook wrapping MediaRecorder with a clean start/stop API.
 *
 * Returns:
 *   - state: 'idle' | 'recording' | 'processing' | 'error'
 *   - error: Error | null
 *   - durationSec: current recording length (seconds, integer)
 *   - start() / stop() / cancel()
 *
 * stop() resolves with the final Blob (or null if canceled).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const PREFERRED_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

export function useVoiceRecorder() {
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [durationSec, setDurationSec] = useState(0);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const startedAtRef = useRef(0);

  const stopTick = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const releaseStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const start = useCallback(async () => {
    if (state === 'recording') return;
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('Your browser does not support audio recording');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorderRef.current = rec;
      rec.start();
      startedAtRef.current = Date.now();
      setDurationSec(0);
      tickRef.current = setInterval(() => {
        setDurationSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 500);
      setState('recording');
    } catch (e) {
      setError(e);
      setState('error');
      releaseStream();
    }
  }, [state]);

  const stop = useCallback(() => {
    return new Promise((resolve) => {
      const rec = recorderRef.current;
      if (!rec || rec.state === 'inactive') {
        releaseStream();
        stopTick();
        setState('idle');
        resolve(null);
        return;
      }
      rec.onstop = () => {
        const mime = rec.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        releaseStream();
        stopTick();
        setState('processing');
        resolve(blob);
      };
      rec.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null;
      rec.stop();
    }
    chunksRef.current = [];
    releaseStream();
    stopTick();
    setState('idle');
    setDurationSec(0);
  }, []);

  // Auto-cleanup on unmount
  useEffect(() => () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    releaseStream();
    stopTick();
  }, []);

  const setIdle = useCallback(() => setState('idle'), []);

  return { state, error, durationSec, start, stop, cancel, setIdle };
}
